import { DAILY_REPORTING_API_BASE_URL } from '../config';

/**
 * Daily Reporting API Service
 * Handles communication with the daily reporting microservice for fast daily analytics
 */

class DailyReportingAPI {
  constructor() {
    this.baseURL = DAILY_REPORTING_API_BASE_URL;
  }

  /**
   * Get authorization headers with provided token
   */
  getAuthHeaders(authToken) {
    if (!authToken) {
      throw new Error('Authentication token is required');
    }
    
    return {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Make API request with error handling
   */
  async makeRequest(endpoint, authToken, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        headers: this.getAuthHeaders(authToken),
        ...options
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Daily Reporting API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  /**
   * Get daily school analytics (optimized for single-day queries)
   * @param {string} authToken - JWT authentication token
   * @param {string} schoolId - School ID
   * @param {string} date - Date (YYYY-MM-DD) - optional, defaults to today
   * @returns {Promise<Object>} Daily analytics data
   */
  async getDailySchoolAnalytics(authToken, schoolId, date = null) {
    const params = new URLSearchParams();
    if (date) {
      params.append('date', date);
    }

    const queryString = params.toString() ? `?${params}` : '';
    return await this.makeRequest(`/api/daily/school/${schoolId}${queryString}`, authToken);
  }

  /**
   * Get class analytics for Class Tracking Dashboard
   * @param {string} authToken - JWT authentication token
   * @param {Object} params - Query parameters
   * @param {string} params.classroomId - Classroom ID (required)
   * @param {string} params.startDate - Start date (YYYY-MM-DD) - optional
   * @param {string} params.endDate - End date (YYYY-MM-DD) - optional
   * @param {string} params.schoolId - School ID - optional
   * @param {string} params.periodId - Period ID - optional
   * @returns {Promise<Object>} Class analytics data with student records
   */
  async getClassAnalytics(authToken, params) {
    const queryParams = new URLSearchParams();
    if (params.classroomId) queryParams.append('classroomId', params.classroomId);
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.schoolId) queryParams.append('school_id', params.schoolId);
    if (params.periodId) queryParams.append('periodId', params.periodId);
    
    const query = queryParams.toString();
    return await this.makeRequest(`/api/daily/class-analytics${query ? `?${query}` : ''}`, authToken);
  }
}

// Export a singleton instance
const dailyReportingAPI = new DailyReportingAPI();
export default dailyReportingAPI;